import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - noindex for legal pages
    document.title = 'Terms of Service | Evernorth AI';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms of Service for Evernorth AI. Read our terms and conditions for using our services.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/terms-of-service`);
    }

    // Add noindex meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (metaRobots && metaRobots.parentNode) {
        metaRobots.setAttribute('content', 'index, follow');
      }
    };
  }, []);

  return (
    <PageLayout>
      <PageHero
        badge="EVERNORTH AI"
        title="Terms of Service"
        titleHighlight=""
        description=""
      />

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gold/10 border border-gold/20 rounded-lg p-6 mb-12">
            <p className="text-navy text-sm">
              <strong>Last updated:</strong> January 2026
            </p>
            <p className="text-navy/80 text-sm mt-2">
              These Terms of Service govern your use of the Evernorth AI website and any services we provide. By using the site or working with us, you agree to these terms.
            </p>
          </div>

          <div className="space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">1</span>
                Services Provided
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Evernorth AI offers:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AI training workshops</li>
                  <li>Introductory and advanced AI education</li>
                  <li>Workflow design services</li>
                  <li>Consulting and operational support</li>
                </ul>
                <p>Details about each service are available on our website or provided directly during sales conversations.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">2</span>
                Accuracy of Information
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>We work to keep the information on our site accurate and up to date.</p>
                <p>We make no guarantees that all content is free from errors.</p>
                <p>We may change offerings, pricing, or content at any time.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">3</span>
                Use of Website
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>You agree to use our website in a lawful and respectful manner.</p>
                <p>You may not attempt to disrupt or damage the site.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">4</span>
                Payments and Refunds
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Workshops, audits, and consulting work must be paid in full before the session or project begins unless otherwise stated in writing.</p>
                <p>Refunds are not provided. If an event must be rescheduled, we ask for at least 24 hours notice. We will work with you in good faith to find a new time and accommodate.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">5</span>
                Professional Advice Disclaimer
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Evernorth AI provides training and consulting for educational and operational improvement purposes. We do not provide legal, financial, or regulatory advice.</p>
                <p>You are responsible for decisions made based on our training and recommendations. We do not and cannot guarantee results of any kind.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">6</span>
                Limitation of Liability
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Evernorth AI is not liable for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Any indirect or incidental damages</li>
                  <li>Loss of data</li>
                  <li>Business interruption</li>
                  <li>Outcomes of workflows or tools used in your organization</li>
                </ul>
                <p>Our total liability for any claim will not exceed the amount paid for the service in question.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">7</span>
                Intellectual Property
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>All materials provided during workshops, training, or consulting are the copyright protected property of Evernorth AI unless otherwise stated.</p>
                <p>Clients may use materials for internal purposes only.</p>
                <p>You may not copy, resell, or distribute our content without permission.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">8</span>
                External Tools and Integrations
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Our services may include the use of third party platforms such as AI tools, scheduling tools, email systems, or workflow tools.</p>
                <p>We are not responsible for the performance, security, or policies of these platforms.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">9</span>
                Termination
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>We reserve the right to terminate access to our site or services for misuse, violation of terms, harmful behavior, or any reason deemed by Evernorth AI.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">10</span>
                Governing Law
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>These terms are governed by the laws of the State of Georgia.</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">11</span>
                Contact Information
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>For questions about these Terms of Service, contact:</p>
                <div className="bg-navy/5 rounded-lg p-6 mt-4">
                  <p className="font-bold text-navy">Evernorth AI</p>
                  <p className="text-navy/80">info@evernorthai.com</p>
                  <p className="text-navy/80">Northwest Georgia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gold/10 border border-gold/20 rounded-lg">
            <p className="text-navy/80 text-sm leading-relaxed">
              By using our website or services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
